import { ArrowBack, Funnel, Refresh } from "@raresail/react-ionicons";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TListHook } from "../../@types/common";
import { PRIMARY_LIGHT } from "../../constants/colors";
import { VIEW_ALL } from "../../constants/constants";
import useGet from "../../hooks/common/useGet";
import { useTitle } from "../../hooks/common/useTitle";
import { listHooks } from "../../hooks/handlers/registerHooks";
import { getListRoute, unsetKeys } from "../../utils/helpers";
import { Loader, SimpleLoader } from "../common/Loader";
import {
  CounterView,
  EmptyView,
  Heading,
  SmallButton,
} from "../elements/Elements";
import { openBottomSheet } from "../../utils/modalUtils";
import {
  updateGlobalContext,
  useGlobalContext,
} from "../context/GlobalContext";
import { PaginationView } from "../pagination/PaginationView";
import { Button } from "../common/Button";
import styled from "styled-components";
import { IconButton } from "@mui/material";

export const ListHandler = ({
  source,
  reload,
  summary,
  title: titleProp,
  state,
  enablePadding = true,
}: {
  source: string | any;
  reload?: boolean;
  summary?: boolean;
  title?: string;
  state?: any;
  enablePadding?: boolean;
}) => {
  const hook = listHooks?.[source];
  const {
    title: hookTitle,
    subtitle,
    ListItem,
    listData,
    defaultValues = [],
    api,
    params,
    filters: filterFields,
    action,
    listType,
    getList,
    showBack,
  }: TListHook = hook ? hook({ state }) : {};

  const title = titleProp || hookTitle || source;
  useTitle(title, !summary);

  const { loadData, loading, data } = useGet();
  const [list, setList] = useState(defaultValues);
  const [filter, setFilter] = useState<any>({});
  const context = useGlobalContext();
  const reloadKey = `reload_${source}`;
  const triggerReload = context?.[reloadKey] || reload; // reload source key

  // GET DATA
  const getData = () => {
    if (api)
      loadData({
        api,
        params: { ...params, page_size: summary ? 4 : 10, ...filter },
        onSuccess: (data: any) =>
          setList(getList ? getList(data) : data?.results),
      });
  };

  useEffect(() => {
    getData();
  }, [filter]);

  useEffect(() => {
    if (triggerReload) getData();
  }, [triggerReload]);

  const showFilters = () => {
    openBottomSheet({
      component: "filters",
      fields: filterFields,
      filter,
      setFilter,
    });
  };

  const ReloadButton = () => (
    <IconButton size="small" color="primary" onClick={() => reloadData(source)}>
      <Refresh />
    </IconButton>
  );

  const BackButton = () => {
    const navigate = useNavigate();
    return (
      <IconButton
        size="small"
        color="primary"
        onClick={() => navigate(-1)}
        style={{ marginRight: 10 }}
      >
        <ArrowBack />
      </IconButton>
    );
  };

  const FilterOption = () => (
    <>
      {filterFields && !summary && (
        <SmallButton
          icon={Funnel}
          onClick={showFilters}
          suffix={
            <CounterView
              value={
                Object.entries(
                  unsetKeys(filter, ["page"]) // remove page
                )?.length
              }
            />
          }
        >
          Filters
        </SmallButton>
      )}
    </>
  );

  // GRID LISTS
  if (listType === "grid") {
    if (loading)
      return (
        <div className="vh100Min flexCenter">
          <Loader />
        </div>
      );

    return (
      <div className="flexColumn gap15 p5">
        <div className="flexNullCenter">
          {showBack && <BackButton />}
          <Heading subtitle={subtitle}>{title}</Heading>

          {/* RELOAD */}
          <ReloadButton />

          {/* FILTER */}
          <FilterOption />
        </div>

        {list?.length > 0 ? (
          <>
            {/* CONTENT */}
            <GridWrapper className="grid gap15">
              {(listData ? listData(list) : list)?.map((v, i) => (
                // RENDER ITEMS
                <div
                  key={i}
                  data-aos="fade-up"
                  data-aos-duration="1000"
                  data-aos-offset={i * -50}
                  data-aos-delay={i * 100}
                >
                  <ListItem data={v} key={i} />
                </div>
              ))}
            </GridWrapper>

            {/* FOOTER */}
            {!summary && (
              <div className="whiteBg r10 p10">
                <PaginationView
                  total={data?.count}
                  setFilter={setFilter}
                  current={filter?.page}
                />
              </div>
            )}
          </>
        ) : (
          <div className="flexCenter" style={{ height: "calc(100vh - 200px)" }}>
            <EmptyView source={source} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`whiteBg r10 hidden ${enablePadding ? "p15" : ""}`}>
      {/* HEADER */}
      <div
        className="flexNullCenter mb15 gap10"
        style={{
          borderBottom: `1.2px solid ${PRIMARY_LIGHT}`,
          paddingBottom: 10,
        }}
      >
        <Heading subtitle={subtitle}>{title}</Heading>

        {/* RELOAD */}
        <ReloadButton />

        {action && !summary && (
          <Button
            className="font12"
            style={{ height: 28, padding: "0 10px" }}
            onClick={action?.onClick}
          >
            <span className="font16" style={{ marginTop: -2 }}>
              +
            </span>{" "}
            New
          </Button>
        )}

        {summary && (
          <Link to={getListRoute(source)} className="main semiBold font13">
            {VIEW_ALL}
          </Link>
        )}

        {/* FILTER */}
        <FilterOption />
      </div>

      {/* CONTENT */}
      <div className="flexColumn gap15">
        {/* LOADER */}
        {loading ? (
          <SimpleLoader />
        ) : list?.length > 0 ? (
          (listData ? listData(list) : list)?.map((v, i) => (
            // RENDER ITEMS
            <div
              key={i}
              data-aos="fade-up"
              data-aos-duration="1000"
              data-aos-offset={i * -50}
              data-aos-delay={i * 100}
            >
              <ListItem data={v} key={i} />
            </div>
          ))
        ) : (
          <EmptyView source={source} />
        )}
      </div>

      {/* FOOTER */}
      {!summary && (
        <div
          style={{
            marginTop: 15,
            paddingTop: 10,
            borderTop: `1.2px solid ${PRIMARY_LIGHT}`,
          }}
        >
          <PaginationView
            total={data?.count}
            setFilter={setFilter}
            current={filter?.page}
          />
        </div>
      )}
    </div>
  );
};

const GridWrapper = styled.div`
  grid-template-columns: 1fr 1fr;
`;

export const reloadData = (source: any, reloadKey?: any) => {
  updateGlobalContext({
    [reloadKey || `reload_${source}`]: new Date(), // update when new id is created
  });
};
